import { useEffect, useRef, useState, type RefObject } from 'react';
import { api, ApiError } from '../lib/api.js';
import type { TranscriptSegment } from '../screens/CallCenter.js';

type Speaker = 'agent' | 'caller';

interface CaptureHandle {
  stop: () => void;
}

export function useCallTranscription(options: {
  enabled: boolean;
  callId: string | null;
  active: boolean;
  agentMuted: boolean;
  remoteAudioRef: RefObject<HTMLAudioElement | null>;
}) {
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [status, setStatus] = useState<'idle' | 'starting' | 'listening' | 'delayed'>('idle');
  const [error, setError] = useState<string | null>(null);
  const mutedRef = useRef(options.agentMuted);
  const lastCallId = useRef<string | null>(null);
  const callStartedAt = useRef(0);
  const sequence = useRef<Record<Speaker, number>>({ agent: 0, caller: 0 });
  mutedRef.current = options.agentMuted;

  useEffect(() => {
    if (options.callId && options.callId !== lastCallId.current) {
      lastCallId.current = options.callId;
      callStartedAt.current = Date.now();
      sequence.current = { agent: 0, caller: 0 };
      setSegments([]);
      setError(null);
    }
  }, [options.callId]);

  useEffect(() => {
    if (!options.enabled || !options.callId || !options.active) {
      setStatus('idle');
      return;
    }
    let cancelled = false;
    const handles: CaptureHandle[] = [];
    const ownedStreams: MediaStream[] = [];
    const callId = options.callId;
    const queues: Record<Speaker, Promise<void>> = {
      agent: Promise.resolve(), caller: Promise.resolve(),
    };

    const submit = (speaker: Speaker, wav: ArrayBuffer, startedMs: number, endedMs: number) => {
      const currentSequence = sequence.current[speaker]++;
      queues[speaker] = queues[speaker].then(async () => {
        try {
          const response = await api.createCallCenterTranscriptSegment(callId, {
            speaker,
            sequence: currentSequence,
            startedMs,
            endedMs,
            audioBase64: arrayBufferToBase64(wav),
          });
          if (response.segment && !cancelled) {
            setSegments((current) => [...current, response.segment!]
              .sort((a, b) => a.startedMs - b.startedMs || a.speaker.localeCompare(b.speaker)));
            setStatus('listening');
          }
        } catch (cause) {
          if (cancelled) return;
          setStatus('delayed');
          setError(transcriptionError(cause));
        }
      });
    };

    void (async () => {
      setStatus('starting');
      setError(null);
      try {
        const agentStream = await navigator.mediaDevices.getUserMedia({
          audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
          video: false,
        });
        if (cancelled) {
          agentStream.getTracks().forEach((track) => track.stop());
          return;
        }
        ownedStreams.push(agentStream);
        handles.push(await startPcmCapture(agentStream, 'agent', callStartedAt.current, submit,
          () => mutedRef.current));

        const remoteStream = await waitForRemoteStream(options.remoteAudioRef, () => cancelled);
        if (!remoteStream || cancelled) throw new Error('Caller audio is not available for captions.');
        handles.push(await startPcmCapture(remoteStream, 'caller', callStartedAt.current, submit,
          () => false));
        if (!cancelled) setStatus('listening');
      } catch (cause) {
        if (!cancelled) {
          setStatus('delayed');
          setError(transcriptionError(cause));
        }
      }
    })();

    return () => {
      cancelled = true;
      handles.forEach((handle) => handle.stop());
      ownedStreams.forEach((stream) => stream.getTracks().forEach((track) => track.stop()));
    };
  }, [options.enabled, options.callId, options.active, options.remoteAudioRef]);

  return { segments, status, error };
}

async function waitForRemoteStream(
  audioRef: RefObject<HTMLAudioElement | null>, cancelled: () => boolean,
): Promise<MediaStream | null> {
  for (let attempt = 0; attempt < 25 && !cancelled(); attempt += 1) {
    const stream = audioRef.current?.srcObject;
    if (stream instanceof MediaStream && stream.getAudioTracks().length > 0) return stream;
    await new Promise((resolve) => window.setTimeout(resolve, 200));
  }
  return null;
}

async function startPcmCapture(
  stream: MediaStream,
  speaker: Speaker,
  callStartedAt: number,
  submit: (speaker: Speaker, wav: ArrayBuffer, startedMs: number, endedMs: number) => void,
  muted: () => boolean,
): Promise<CaptureHandle> {
  const context = new AudioContext();
  await context.resume();
  const source = context.createMediaStreamSource(stream);
  const processor = context.createScriptProcessor(4096, 1, 1);
  const silentOutput = context.createGain();
  silentOutput.gain.value = 0;
  source.connect(processor);
  processor.connect(silentOutput);
  silentOutput.connect(context.destination);

  const chunks: Float32Array[] = [];
  let sampleCount = 0;
  const requiredSamples = Math.floor(context.sampleRate * 4);
  const emitBufferedAudio = () => {
    if (sampleCount === 0) return;
    const bufferedSampleCount = sampleCount;
    const combined = new Float32Array(sampleCount);
    let offset = 0;
    for (const chunk of chunks) {
      combined.set(chunk, offset);
      offset += chunk.length;
    }
    chunks.length = 0;
    sampleCount = 0;
    const endedMs = Math.min(86_400_000, Math.max(1, Date.now() - callStartedAt));
    const durationMs = Math.max(1, Math.round(bufferedSampleCount / context.sampleRate * 1_000));
    const startedMs = Math.max(0, endedMs - durationMs);
    submit(speaker, encodePcmWav(downsample(combined, context.sampleRate, 16_000), 16_000),
      startedMs, endedMs);
  };
  processor.onaudioprocess = (event) => {
    if (speaker === 'agent' && muted()) return;
    const samples = new Float32Array(event.inputBuffer.getChannelData(0));
    chunks.push(samples);
    sampleCount += samples.length;
    if (sampleCount >= requiredSamples) emitBufferedAudio();
  };

  return { stop: () => {
    processor.onaudioprocess = null;
    if (sampleCount >= Math.floor(context.sampleRate / 2)) emitBufferedAudio();
    source.disconnect();
    processor.disconnect();
    silentOutput.disconnect();
    void context.close();
  } };
}

function downsample(input: Float32Array, sourceRate: number, targetRate: number): Float32Array {
  if (sourceRate === targetRate) return input;
  const ratio = sourceRate / targetRate;
  const output = new Float32Array(Math.floor(input.length / ratio));
  for (let index = 0; index < output.length; index += 1) {
    const start = Math.floor(index * ratio);
    const end = Math.max(start + 1, Math.floor((index + 1) * ratio));
    let total = 0;
    for (let inputIndex = start; inputIndex < end && inputIndex < input.length; inputIndex += 1) {
      total += input[inputIndex];
    }
    output[index] = total / Math.max(1, end - start);
  }
  return output;
}

function encodePcmWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  writeAscii(view, 0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeAscii(view, 8, 'WAVE');
  writeAscii(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeAscii(view, 36, 'data');
  view.setUint32(40, samples.length * 2, true);
  for (let index = 0; index < samples.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, samples[index]));
    view.setInt16(44 + index * 2, sample < 0 ? sample * 0x8000 : sample * 0x7fff, true);
  }
  return buffer;
}

function writeAscii(view: DataView, offset: number, value: string) {
  for (let index = 0; index < value.length; index += 1) view.setUint8(offset + index, value.charCodeAt(index));
}

function arrayBufferToBase64(value: ArrayBuffer): string {
  const bytes = new Uint8Array(value);
  let result = '';
  for (let offset = 0; offset < bytes.length; offset += 0x8000) {
    result += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000));
  }
  return btoa(result);
}

function transcriptionError(cause: unknown): string {
  if (cause instanceof ApiError && cause.reason === 'transcription_unavailable') {
    return 'Live captions are delayed. The call can continue while the local speech engine recovers.';
  }
  if (cause instanceof DOMException && cause.name === 'NotAllowedError') {
    return 'Microphone access is required for operator captions.';
  }
  return cause instanceof Error ? cause.message : 'Live captions are temporarily unavailable.';
}
